import { FunctionComponent, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import { usePutUser } from "../../hooks/useUser";
import { USER_ROLE_LABELS } from "../../constants";
import { AircraftAssignmentPicker } from "./AircraftAssignmentPicker";
import { USER_DIALOG_FIELD_GRID_SX } from "./userDialogLayout";

type Props = {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  operators?: Operator[];
  showOperatorSelector?: boolean;
  lockRole?: boolean;
}

type EditUserOperatorValue = {
  id: number | null;
  label: string;
  booked_options: string[];
};

export const EditUserModal: FunctionComponent<Props> = ({
  user,
  isOpen,
  onClose,
  operators = [],
  showOperatorSelector = false,
  lockRole = false,
}) => {
  const {
    setUser,
    email,
    onEmailChange,
    type,
    dbUserType,
    onTypeChange,
    password,
    onPasswordChange,
    tlc,
    pin,
    onPinChange,
    onTlcChange,
    onSubmit,
    message,
    error,
    status,
    role,
    onRoleChange,
    acmiCallsignPrefix,
    onAcmiCallsignPrefixChange,
    availableRoles,
    canSeeAllFlights,
    onCanSeeAllFlightsChange,
    iosSettingsVariant,
    onIosSettingsVariantChange,
    operator,
    onOperatorChange,
    aircraftAssignments,
    onAircraftAssignmentsChange,
  } = usePutUser();

  useEffect(() => {
    setUser(user);
  }, [user]);

  const operatorOptions: EditUserOperatorValue[] =
    operators?.map(({ id, name, booked_options }) => ({
      id,
      label: name,
      booked_options: booked_options || [],
    })) || [];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-labelledby="edit-user-title"
      PaperProps={{ sx: { position: "relative" } }}
    >
      <DialogTitle id="edit-user-title">Edit user</DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto" }}>
        {message ? (
          <Alert severity="success">{message}</Alert>
        ) : (
          <Box component="form" id="edit-user-form" onSubmit={onSubmit} noValidate>
            <Box sx={USER_DIALOG_FIELD_GRID_SX}>
              <FormControl fullWidth>
                <InputLabel id="edit-user-type-label">Type</InputLabel>
                <Select
                  labelId="edit-user-type-label"
                  value={type}
                  onChange={onTypeChange}
                  label="Type"
                  disabled={role === "acmi_client" || role === "refuel_eu_reader"}
                >
                  <MenuItem value={0}>smartEFB</MenuItem>
                  <MenuItem value={1}>FlightViewer</MenuItem>
                  <MenuItem value={2}>smartEFB + FlightViewer</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="edit-user-role-label">Role</InputLabel>
                <Select labelId="edit-user-role-label" value={role} onChange={onRoleChange} label="Role" disabled={lockRole}>
                  {availableRoles.map((value) => (
                    <MenuItem key={value} value={value}>
                      {USER_ROLE_LABELS[value]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showOperatorSelector && (
                <Autocomplete
                  disableClearable
                  options={operatorOptions}
                  value={operator}
                  onChange={onOperatorChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ width: "100%", minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField {...params} error={!!error} label="Operator" />
                  )}
                />
              )}
              <TextField
                value={email}
                error={!!error}
                label="Email"
                onChange={onEmailChange}
                fullWidth
              />
              {role === "acmi_client" && (
                <TextField
                  value={acmiCallsignPrefix}
                  error={!!error}
                  label="Callsign prefix"
                  onChange={onAcmiCallsignPrefixChange}
                  fullWidth
                />
              )}
              {(type == 0 || type == 2) && (
                <TextField
                  value={tlc}
                  error={!!error}
                  label="TLC"
                  onChange={onTlcChange}
                  fullWidth
                />
              )}
              {(type == 0 || type == 2) && (
                <TextField
                  value={pin}
                  error={!!error}
                  label={dbUserType !== 1 ? "New PIN (optional)" : "PIN"}
                  onChange={onPinChange}
                  fullWidth
                />
              )}
              {type > 0 && (
                <TextField
                  value={password}
                  error={!!error}
                  label={dbUserType !== 0 ? "New password (optional)" : "Password"}
                  onChange={onPasswordChange}
                  fullWidth
                />
              )}
              {(type == 0 || type == 2) && (
                <FormControl fullWidth>
                  <InputLabel>iOS variant</InputLabel>
                  <Select
                    value={iosSettingsVariant}
                    onChange={onIosSettingsVariantChange}
                    label="iOS variant"
                  >
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="test">Test</MenuItem>
                  </Select>
                </FormControl>
              )}
              {(type == 0 || type == 2) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={canSeeAllFlights}
                      onChange={onCanSeeAllFlightsChange}
                    />
                  }
                  label="Can see all flights (app)"
                />
              )}
              {role !== "refuel_eu_reader" && <Box sx={{ gridColumn: "1 / -1" }}>
                <AircraftAssignmentPicker
                  value={aircraftAssignments}
                  onChange={onAircraftAssignmentsChange}
                  operatorRef={operator.id}
                />
              </Box>}
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!message && (
          <Button type="submit" form="edit-user-form" variant="contained">
            Submit
          </Button>
        )}
        <Button onClick={onClose} variant={message ? "contained" : "outlined"}>
          Close
        </Button>
      </DialogActions>
      <Backdrop sx={{ position: "absolute", zIndex: 1 }} open={status === "loading"}>
        <CircularProgress />
      </Backdrop>
    </Dialog>
  );
};
